import type {
  DeviceCatalogRecord,
  DeviceImageAsset,
  DeviceImageKind,
} from './types';

export const DEVICE_IMAGE_MAX_COUNT = 6;
/** Test-period browser Mock guard; not a formal product upload limit. */
export const DEVICE_IMAGE_MAX_FILE_BYTES = 512 * 1024;
/** Test-period per-device browser Mock guard; not a formal product upload limit. */
export const DEVICE_IMAGE_MAX_TOTAL_BYTES = 3 * 1024 * 1024;
/** Business-state guard only; device asset bytes are stored outside this payload. */
export const MOCK_STORAGE_MAX_CHARS = 4 * 1024 * 1024;
export const DEVICE_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';
export const DEVICE_ASSET_REF_PREFIX = 'cpq-device-asset://';

const ALLOWED_LOCAL_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

interface ImageFileMetadata {
  name: string;
  type: string;
  size: number;
}

type LegacyDeviceMedia = Pick<DeviceCatalogRecord, 'id' | 'image' | 'gallery'> &
  Partial<Pick<DeviceCatalogRecord, 'images' | 'primaryImageId'>>;

function stableHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function stableDeviceImageId(
  deviceId: string,
  source: string,
  order: number,
) {
  return `IMG-${stableHash(`${deviceId}\u0000${order}\u0000${source}`)}`;
}

function inferMimeType(source: string) {
  const match = source.match(/^data:([^;,]+)/i);
  return match?.[1]?.toLowerCase() ?? '';
}

function estimateSourceBytes(source: string) {
  const comma = source.indexOf(',');
  if (!source.startsWith('data:') || comma < 0) return 0;
  const payload = source.slice(comma + 1);
  if (source.slice(0, comma).includes(';base64')) {
    return Math.max(0, Math.floor((payload.length * 3) / 4));
  }
  try {
    return new TextEncoder().encode(decodeURIComponent(payload)).length;
  } catch {
    return new TextEncoder().encode(payload).length;
  }
}

function legacyImageName(source: string, order: number) {
  if (/^https?:\/\//i.test(source)) {
    try {
      const segment = new URL(source).pathname
        .split('/')
        .filter(Boolean)
        .at(-1);
      if (segment) return decodeURIComponent(segment);
    } catch {
      // Fall through to a deterministic legacy label.
    }
  }
  return order === 0 ? '历史主图' : `历史图片 ${order + 1}`;
}

export function createDeviceImageAsset({
  deviceId,
  source,
  name,
  mimeType,
  size,
  order,
  kind,
  id,
  assetId,
}: {
  deviceId: string;
  source: string;
  name: string;
  mimeType: string;
  size: number;
  order: number;
  kind: DeviceImageKind;
  id?: string;
  assetId?: string;
}): DeviceImageAsset {
  return {
    id:
      id ??
      stableDeviceImageId(
        deviceId,
        assetId ? createDeviceAssetReference(assetId) : source,
        order,
      ),
    assetId,
    source,
    name,
    mimeType,
    size,
    order,
    kind,
  };
}

export function createDeviceAssetReference(assetId: string) {
  return `${DEVICE_ASSET_REF_PREFIX}${assetId}`;
}

export function isDeviceAssetReference(source: string) {
  return source.startsWith(DEVICE_ASSET_REF_PREFIX);
}

export function assetIdFromDeviceAssetReference(source: string) {
  return isDeviceAssetReference(source)
    ? source.slice(DEVICE_ASSET_REF_PREFIX.length) || null
    : null;
}

function assertDeviceImages(images: DeviceImageAsset[]) {
  if (images.length > DEVICE_IMAGE_MAX_COUNT) {
    throw new Error(`每台设备最多添加 ${DEVICE_IMAGE_MAX_COUNT} 张图片`);
  }
  const ids = new Set<string>();
  const sources = new Set<string>();
  let localBytes = 0;
  for (const image of images) {
    if (!image.id || !image.source || !image.name) {
      throw new Error('设备图片缺少稳定身份或来源');
    }
    if (image.assetId !== undefined && !image.assetId.trim()) {
      throw new Error('设备图片资产引用无效');
    }
    if (ids.has(image.id) || sources.has(image.source)) {
      throw new Error('不能重复添加同一张设备图片');
    }
    if (!Number.isInteger(image.order) || image.order < 0) {
      throw new Error('设备图片顺序无效');
    }
    if (!Number.isFinite(image.size) || image.size < 0) {
      throw new Error('设备图片大小无效');
    }
    if (image.kind === 'local-file') {
      if (!ALLOWED_LOCAL_IMAGE_TYPES.has(image.mimeType)) {
        throw new Error('本地设备图片仅支持 JPEG、PNG 或 WebP');
      }
      if (image.size > DEVICE_IMAGE_MAX_FILE_BYTES) {
        throw new Error(
          '测试期单张设备图片不能超过 512 KiB（不是正式产品上限）',
        );
      }
      localBytes += image.size;
    }
    ids.add(image.id);
    sources.add(image.source);
  }
  if (localBytes > DEVICE_IMAGE_MAX_TOTAL_BYTES) {
    throw new Error('测试期单台设备图片总量不能超过 3 MiB（不是正式产品上限）');
  }
}

export function projectDeviceMedia(
  images: DeviceImageAsset[],
  requestedPrimaryImageId: string | null | undefined,
) {
  assertDeviceImages(images);
  const ordered = [...images]
    .sort(
      (left, right) =>
        left.order - right.order || left.id.localeCompare(right.id),
    )
    .map((image, order) => ({ ...image, order }));
  const primaryImageId = ordered.some(
    (image) => image.id === requestedPrimaryImageId,
  )
    ? (requestedPrimaryImageId ?? null)
    : (ordered[0]?.id ?? null);
  const primary = ordered.find((image) => image.id === primaryImageId);
  return {
    images: ordered,
    primaryImageId,
    image: primary?.source ?? '',
    gallery: ordered.map((image) => image.source),
  };
}

/** Resolve the primary source by the device's stable media identity. */
export function resolveDevicePrimaryImage(
  device: Pick<DeviceCatalogRecord, 'images' | 'primaryImageId'>,
) {
  try {
    const projected = projectDeviceMedia(device.images, device.primaryImageId);
    return projected.primaryImageId && projected.image
      ? projected.image
      : undefined;
  } catch {
    return undefined;
  }
}

/** Resolve the primary 128px source for compact rows without sacrificing the original preview. */
export function resolveDevicePrimaryThumbnail(
  device: Pick<
    DeviceCatalogRecord,
    'images' | 'primaryImageId' | 'image' | 'thumbnail'
  >,
) {
  const primary =
    device.images.find((image) => image.id === device.primaryImageId) ??
    device.images[0];
  return (
    primary?.thumbnailSource ??
    primary?.source ??
    device.thumbnail ??
    device.image
  );
}

export function migrateLegacyDeviceMedia(device: LegacyDeviceMedia) {
  if (Array.isArray(device.images)) {
    return projectDeviceMedia(device.images, device.primaryImageId);
  }
  const sources = [
    ...new Set([device.image, ...device.gallery].filter(Boolean)),
  ];
  const images = sources.map((source, order) =>
    createDeviceImageAsset({
      deviceId: device.id,
      source,
      name: legacyImageName(source, order),
      mimeType: inferMimeType(source),
      size: estimateSourceBytes(source),
      order,
      kind: 'legacy-field',
    }),
  );
  const primary = images.find((image) => image.source === device.image);
  return projectDeviceMedia(images, primary?.id);
}

export function validateImageFile(file: ImageFileMetadata): string | null {
  if (!ALLOWED_LOCAL_IMAGE_TYPES.has(file.type)) {
    return `${file.name} 仅支持 JPEG、PNG 或 WebP`;
  }
  if (file.size > DEVICE_IMAGE_MAX_FILE_BYTES) {
    return `${file.name} 不能超过测试期单图边界 512 KiB（不是正式产品上限）`;
  }
  return null;
}

export function normalizeReplacementIds(
  replacementIds: string[],
  currentDeviceId: string,
  knownDeviceIds: Set<string>,
) {
  const seen = new Set<string>();
  for (const replacementId of replacementIds) {
    if (replacementId === currentDeviceId) {
      throw new Error('平替设备不能选择当前设备');
    }
    if (seen.has(replacementId)) throw new Error('平替设备不能重复');
    if (!knownDeviceIds.has(replacementId)) {
      throw new Error(`平替设备 ${replacementId} 不存在`);
    }
    seen.add(replacementId);
  }
  return [...replacementIds];
}

export function hasValidDeviceMedia(device: DeviceCatalogRecord) {
  try {
    const projected = projectDeviceMedia(device.images, device.primaryImageId);
    return (
      projected.image === device.image &&
      JSON.stringify(projected.gallery) === JSON.stringify(device.gallery) &&
      JSON.stringify(projected.images) === JSON.stringify(device.images)
    );
  } catch {
    return false;
  }
}

export function assertMockStorageCapacity(state: unknown) {
  const persistedShape = projectDeviceMediaForStorage(state);
  if (JSON.stringify(persistedShape).length > MOCK_STORAGE_MAX_CHARS) {
    throw new Error(
      '本地 Mock 存储容量（业务状态）超过测试期 4 MiB 门禁；图片资产不计入该门禁，请减少非媒体演示数据后重试',
    );
  }
}

type MediaStorageShape = {
  catalog?: { devices?: Array<Partial<DeviceCatalogRecord>> };
  quotes?: Record<
    string,
    {
      items?: Array<{
        deviceFieldSnapshot?: { image?: string };
      }>;
      itemAdjustments?: Array<{
        originalItemSnapshot?: { deviceFieldSnapshot?: { image?: string } };
        effectiveItemSnapshot?: {
          deviceFieldSnapshot?: { image?: string } | undefined;
        } | null;
      }>;
    }
  >;
  shares?: MediaSnapshotOwner[];
  publications?: MediaSnapshotOwner[];
};

type MediaSnapshotOwner = {
  itemsSnapshot?: Array<{ itemId: string; image?: string }>;
  selectionOptionsSnapshot?: Array<{ itemId: string; image?: string }>;
  quotationListProjection?: {
    fields?: Array<{ fieldId: string; presentation?: string }>;
    rows?: Array<{
      values?: Record<string, { value?: unknown }>;
    }>;
  };
};

/**
 * Convert only asset-backed runtime sources to stable references before the
 * business state is measured or written. Legacy/fixture strings remain
 * readable until the browser asset migration has an opportunity to copy them.
 */
export function projectDeviceMediaForStorage<T>(state: T): T {
  const next = structuredClone(state) as T & MediaStorageShape;
  const sourceToReference = new Map<string, string>();
  const devices = next.catalog?.devices;
  if (Array.isArray(devices)) {
    for (const device of devices) {
      if (!Array.isArray(device.images)) continue;
      const images = device.images.map((image) => {
        if (!image.assetId) return image;
        const reference = createDeviceAssetReference(image.assetId);
        sourceToReference.set(image.source, reference);
        sourceToReference.set(reference, reference);
        return { ...image, source: reference };
      });
      const projected = projectDeviceMedia(images, device.primaryImageId);
      Object.assign(device, projected);
    }
  }

  const rewriteSnapshot = (snapshot?: { image?: string }) => {
    if (!snapshot?.image) return;
    const reference = sourceToReference.get(snapshot.image);
    if (reference) snapshot.image = reference;
  };
  for (const quote of Object.values(next.quotes ?? {})) {
    for (const item of quote.items ?? [])
      rewriteSnapshot(item.deviceFieldSnapshot);
    for (const adjustment of quote.itemAdjustments ?? []) {
      rewriteSnapshot(adjustment.originalItemSnapshot?.deviceFieldSnapshot);
      rewriteSnapshot(adjustment.effectiveItemSnapshot?.deviceFieldSnapshot);
    }
  }
  const rewriteSnapshotOwner = (owner: MediaSnapshotOwner) => {
    for (const item of owner.itemsSnapshot ?? []) rewriteSnapshot(item);
    for (const item of owner.selectionOptionsSnapshot ?? [])
      rewriteSnapshot(item);
    const projection = owner.quotationListProjection;
    const imageFieldIds = new Set(
      (projection?.fields ?? [])
        .filter((field) => field.presentation === 'image')
        .map((field) => field.fieldId),
    );
    for (const row of projection?.rows ?? []) {
      for (const fieldId of imageFieldIds) {
        const cell = row.values?.[fieldId];
        if (!cell || typeof cell.value !== 'string') continue;
        const reference = sourceToReference.get(cell.value);
        if (reference) cell.value = reference;
      }
    }
  };
  for (const share of next.shares ?? []) rewriteSnapshotOwner(share);
  for (const publication of next.publications ?? [])
    rewriteSnapshotOwner(publication);
  return next;
}
